import {Box, HStack, FlexProps, VStack, Text, useMultiStyleConfig, Link, StackProps, BoxProps, TextProps} from '@chakra-ui/react'
import {Picture, Video, Button, Timer, IconButton, Icon, IconProps} from '_uiComponents'
import React, {useEffect, useRef, useState} from 'react'
import {useAnalytics, usePageDesigner, useViewport} from '_customHooks'
import {createRedirectUrl, updateCloudinaryPDVideo, updateCloudinaryPDImage, PAGE_DESIGNER_MODULES, setStickyBannerCookie, hasStickyBannerCookie} from '_utils'
import {CLDImageAsset, CLDVideoAsset} from '_types'

interface StickyBannerProps {
    imgDesk?: CLDImageAsset
    imgTablet?: CLDImageAsset
    imgMobile?: CLDImageAsset
    videoDesk?: CLDVideoAsset
    videoMobile?: CLDVideoAsset
    title?: string
    subtitle?: string
    text?: string
    countdownTimer?: string
    ctaText?: string
    ctaLink?: string
    colorContent?: string
    backgroundColor?: string
    classes?: string
    extraCSS?: string
    cookieTime?: string
    cookieID?: string
    analyticsID?: string
    position: number
    analyticsName?: string
}

const StickyBanner = ({
    imgDesk,
    imgTablet,
    imgMobile,
    videoDesk,
    videoMobile,
    title,
    subtitle,
    text,
    countdownTimer,
    ctaText,
    ctaLink,
    colorContent,
    backgroundColor,
    classes,
    extraCSS,
    cookieTime = '365',
    cookieID,
    analyticsID,
    position,
    analyticsName,
}: StickyBannerProps): JSX.Element => {
    const styles = useMultiStyleConfig('StickyBanner', {backgroundColor: backgroundColor?.trim(), colorContent: colorContent?.trim()}) as Record<
        string,
        BoxProps & StackProps & FlexProps & TextProps & IconProps
    >
    const {linkObject, globalImageTransformation, globalVideoTransformation, CloudinaryPageDesignerCNAME} = usePageDesigner()
    const [isBannerShow, setIsBannerShow] = useState(true)
    const [isDisplaySet, setIsDisplaySet] = useState(false)
    const {analyticsEventPromoView, analyticsEventPromoClick} = useAnalytics()
    const targetElement = useRef(null)
    const {isMobile} = useViewport()
    const playerDeskConf = videoDesk && JSON.parse(videoDesk.playerConf)
    const playerMobileConf = videoMobile && JSON.parse(videoMobile.playerConf)
    const isControlDesk = playerDeskConf && playerDeskConf.playerConfig?.controls
    const isAutoplayDesk = playerDeskConf && playerDeskConf.playerConfig?.autoplay
    const isLoopDesk = playerDeskConf && playerDeskConf.playerConfig?.loop
    const isMuteDesk = playerDeskConf && playerDeskConf.playerConfig?.muted
    const isControlMobile = playerMobileConf && playerMobileConf.playerConfig?.controls
    const isAutoplayMobile = playerMobileConf && playerMobileConf.playerConfig?.autoplay
    const isLoopMobile = playerMobileConf && playerMobileConf.playerConfig?.loop
    const isMuteMobile = playerMobileConf && playerMobileConf.playerConfig?.muted

    const triggerAnalyticsEventPromoClick = (textBtn: string): void => {
        analyticsEventPromoClick({
            id: analyticsID ? analyticsID : 'null',
            position: String(position),
            creative: imgDesk ? PAGE_DESIGNER_MODULES.STICKY_BANNER : PAGE_DESIGNER_MODULES.STICKY_BANNER_VIDEO,
            promoText: textBtn,
            name: analyticsName ? analyticsName : 'null',
        })
    }

    useEffect(() => {
        if (cookieID) {
            setIsBannerShow(!hasStickyBannerCookie(cookieID))
            setIsDisplaySet(true)
        }
    }, [cookieID])

    useEffect(() => {
        if (cookieID) {
            if (!isBannerShow) {
                setStickyBannerCookie(cookieID, cookieTime)
            }
        }
    }, [cookieID])

    useEffect(() => {
        if (isDisplaySet) {
            analyticsEventPromoView({
                id: analyticsID ? analyticsID : 'null',
                position: isBannerShow ? position - 1 : position,
                name: isBannerShow ? (analyticsName ? analyticsName : 'null') : undefined,
                creative: imgDesk ? PAGE_DESIGNER_MODULES.STICKY_BANNER : PAGE_DESIGNER_MODULES.STICKY_BANNER_VIDEO,
            })
        }
    }, [isDisplaySet])

    return (
        <>
            {isBannerShow && (
                <Box {...styles.wrapper} data-testid="sticky-banner" css={extraCSS} ref={targetElement}>
                    <Box className={`${classes} cc-pd cc-pd-sticky-banner`}>
                        <Box className="cc-object-container">
                            <Box {...styles.btnClose} className="cs-close-btn">
                                <IconButton
                                    className="cc-icon"
                                    icon={
                                        <Icon name="IconCloseLight" size="lg" color={colorContent && colorContent.trim().length ? colorContent : undefined} />
                                    }
                                    size={'iconButtonMd'}
                                    isRounded={false}
                                    background="color.background.transparent"
                                    onClick={() => {
                                        setIsBannerShow(false)
                                        if (cookieID) {
                                            setStickyBannerCookie(cookieID, cookieTime)
                                        }
                                    }}
                                    data-testid="sticky-banner-close"
                                    aria-label="Close sticky banner PLP"
                                />
                            </Box>

                            <HStack {...styles.container} className="cc-element">
                                <Box
                                    sx={styles.image}
                                    className={`cc-content-${imgDesk ? 'image' : `video cc-content-video-01 cc-video-0 cc-content-video-${isMobile ? 'mobile cc-content-video-mobile-cloudinary' : 'desktop'}`}`}
                                >
                                    {imgDesk ? (
                                        <Picture
                                            className="cc-img-wrp"
                                            objectFit="cover"
                                            imagesm={updateCloudinaryPDImage(
                                                imgMobile || imgTablet || imgDesk,
                                                globalImageTransformation,
                                                CloudinaryPageDesignerCNAME,
                                            )}
                                            imagemd={updateCloudinaryPDImage(imgTablet || imgDesk, globalImageTransformation, CloudinaryPageDesignerCNAME)}
                                            imagelg={updateCloudinaryPDImage(imgDesk, globalImageTransformation, CloudinaryPageDesignerCNAME)}
                                            imagexl={updateCloudinaryPDImage(imgDesk, globalImageTransformation, CloudinaryPageDesignerCNAME)}
                                            height={{base: '100%', md: 'fit-content'}}
                                        />
                                    ) : (
                                        <>
                                            {isMobile ? (
                                                <Video
                                                    autoplay={isAutoplayMobile || isAutoplayDesk}
                                                    controls={isControlMobile || isControlDesk}
                                                    muted={isMuteMobile || isMuteDesk}
                                                    loop={isLoopMobile || isLoopDesk}
                                                    videoxs={
                                                        videoMobile
                                                            ? updateCloudinaryPDVideo(videoMobile, globalVideoTransformation, CloudinaryPageDesignerCNAME)
                                                            : ''
                                                    }
                                                    videolg={
                                                        videoDesk
                                                            ? updateCloudinaryPDVideo(videoDesk, globalVideoTransformation, CloudinaryPageDesignerCNAME)
                                                            : ''
                                                    }
                                                    zIndex={1}
                                                    objectFit="cover"
                                                ></Video>
                                            ) : (
                                                <Video
                                                    autoplay={isAutoplayDesk}
                                                    controls={isControlDesk}
                                                    muted={isMuteMobile}
                                                    loop={isLoopDesk}
                                                    videolg={
                                                        videoDesk
                                                            ? updateCloudinaryPDVideo(videoDesk, globalVideoTransformation, CloudinaryPageDesignerCNAME)
                                                            : ''
                                                    }
                                                    zIndex={1}
                                                    objectFit="cover"
                                                ></Video>
                                            )}
                                        </>
                                    )}
                                </Box>

                                {title && (
                                    <HStack {...styles.info} className="cc-content-info">
                                        <VStack {...styles.header} className="cc-section-title">
                                            {title && (
                                                <Text {...styles.title} className="cc-title">
                                                    {title}
                                                </Text>
                                            )}
                                            {subtitle && (
                                                <Text {...styles.subtitle} className="cc-subtitle">
                                                    {subtitle}
                                                </Text>
                                            )}
                                        </VStack>

                                        {(countdownTimer || text) && countdownTimer
                                            ? new Date() < new Date(countdownTimer) && (
                                                  <Box {...styles.body} {...styles.countdownContainer} data-testid="sticky-banner-countdown" className="cc-section-text">
                                                      <Box {...styles.countdownWrapper}>
                                                          <Timer {...styles.countdown} time={countdownTimer} compact />
                                                      </Box>
                                                  </Box>
                                              )
                                            : text && (
                                                  <Box {...styles.body} className="cc-section-text">
                                                      <Text {...styles.text} className="cc-text">
                                                          {text}
                                                      </Text>
                                                  </Box>
                                              )}
                                    </HStack>
                                )}

                                {ctaLink && ctaText && (
                                    <Box sx={styles.cta} className="cc-content-button">
                                        <Button
                                            className="cc-button"
                                            data-testid="sticky-banner-btn"
                                            as={Link}
                                            size="sm"
                                            onClick={() => triggerAnalyticsEventPromoClick(ctaText)}
                                            variant={'primary'}
                                            href={createRedirectUrl(ctaLink, linkObject.hostlink, linkObject.locale, linkObject.site)}
                                        >
                                            {ctaText}
                                        </Button>
                                    </Box>
                                )}
                            </HStack>
                        </Box>
                    </Box>
                </Box>
            )}
        </>
    )
}

export {StickyBanner}
